/**
 * Licensed Materials - Property of IBM
 * IBM Watson Analytics (C) Copyright IBM Corp. 2018, 2019
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'underscore',
	'../../utils/DatasourceUtil'
], function(
	_,
	DatasourceUtil
) {

	const getMetadataColumnsCallback = () => {
		return true;
	};

	const _getProjectedDataItems =  (dataSlots) => {
		const itemIds= [];
		dataSlots.forEach(slotAPI => {
			const slotDataItems = slotAPI.getDataItemList() || [];
			slotDataItems.forEach(dataItemAPI => {
				itemIds.push(dataItemAPI.getColumnId());
			});
		});
		return itemIds;
	};

	//Collect only the hierarchies that are in the same tables
	const _isSameTable = (tableNames, column) => {
		const colTableName = column.getTableName();
		for(const name of tableNames) {
			if (colTableName === name) {
				return true;
			}
		}
		return false;
	};

	const _retrieveItems = (metadaColumns, nonProjectedItems, matchById, tableNames) => {
		const result = [];
		nonProjectedItems.forEach(value => {
			for(let column of metadaColumns) {
				const columnName = column.getLabel().toLocaleLowerCase();
				if (matchById && column.getId() === value) {
					result.push(columnName);
					break;
				} else if (!matchById && columnName === value && _isSameTable(tableNames, column)) {
					result.push(column.getId());
					break;
				}
			}
		});
		return _.uniq(result);
	};

	const _getColumnNames = (sourceModule, targetModule, tableNames, searchItemIds) => {
		let columnNames = _retrieveItems(sourceModule.getMetadataColumns(getMetadataColumnsCallback), searchItemIds, true, tableNames);
		if (columnNames.length === 0) {
			//The item ids might be in the target module so check it here
			//This case occur when there are multiple viz in same module involve where one vis does not have a projected item but other vis does have the projected item. So
			//depends on who gets to process first, it can get here.
			columnNames = _retrieveItems(targetModule.getMetadataColumns(getMetadataColumnsCallback), searchItemIds, true, tableNames);
		}
		return columnNames;
	};

	class SynchSlotDataItemAPI {
		constructor(options = {}) {
			this.itemId = options.itemId;
		}
		getColumnId () {
			return this.itemId;
		}
	}

	class SynchSlotAPI {
		constructor(options = {}) {
			this.generateDataItemIds(options.itemIds);
			return {
				getDataItemList: () => {
					return this.dataItemAPIs;
				}
			};
		}

		generateDataItemIds(itemIds = []) {
			this.dataItemAPIs = [];
			itemIds.forEach(itemId => {
				this.dataItemAPIs.push(new SynchSlotDataItemAPI({ itemId: itemId }));
			});
		}
	}

	/**
	* Wrapper class representing a visualization's project items
	*/
	class SynchProjectedItem {
		constructor (module, item) {
			if (!item) {
				throw new Error('item object is not provided.');
			}

			this.item = item;
			this.module = module;

			return {
				getLabel: this.getLabel.bind(this),
				getItemId: this.getItemId.bind(this),
				getId: this.getItemId.bind(this),
				getTableName: this.getTableName.bind(this)
			};
		}

		getLabel () {
			return this.item.itemLabel || this.item.getLabel();
		}

		getItemId () {
			return this.item.itemId || this.item.getItemId();
		}

		getTableName () {
			var metadataColumn = this.module.getMetadataColumn(this.getItemId());
			return metadataColumn ? metadataColumn.getTableName() : undefined;
		}
	}

	/**
	* This class represents a synchronize context entry use for both page context origin [visualization | filter] types
	*/
	class SynchContext {

		constructor (options = {}) {
			if (!options.fromObject) {
				throw new Error('fromObject is not provided.');
			}

			this.fromObject = options.fromObject;
			this.module = options.module;
			this.synchDataEntry = options.synchDataEntry;
			this.projectedItems = SynchContextHelper.createSynchProjectedItems(this.module, options.projectedItems);
			this.ownerWidget = options.ownerWidget;
			this.widgetIds = [options.widgetId];
			this.getTableRef();
		}

		getAPI () {
			if (!this.api) {
				this.api = {
					getSourceId: this.getSourceId.bind(this),
					getEventGroupId: this.getEventGroupId.bind(this),
					getEventSourceId: this.getEventSourceId.bind(this),
					getTableRef: this.getTableRef.bind(this),
					getOrigin: this.getOrigin.bind(this),
					getModule: this.getModule.bind(this),
					setModule: this.setModule.bind(this),
					getSynchDataEntry: this.getSynchDataEntry.bind(this),
					getProjectedItems: this.getProjectedItems.bind(this),
					setProjectedItems: this.setProjectedItems.bind(this),
					haveJoinedTables: this.haveJoinedTables.bind(this),

					getSynchronizeItemId: this.synchDataEntry.getSynchronizeItemId.bind(this.synchDataEntry),
					getSynchronizeItems: this.synchDataEntry.getSynchronizeItems.bind(this.synchDataEntry),
					addSynchronizeItem: this.synchDataEntry.addSynchronizeItem.bind(this.synchDataEntry),
					toJSON: this.synchDataEntry.toJSON.bind(this.synchDataEntry),
					getWidgetIds: this.getWidgetIds.bind(this)
				};
			}

			return this.api;
		}

		getSourceId () {
			return this.fromObject.getSourceId();
		}

		getEventGroupId () {
			return this.fromObject.getEventGroupId();
		}

		getEventSourceId () {
			return this.fromObject.getEventSourceId();
		}

		getTableRef () {
			this.tableRef = this.tableRef || (this.fromObject.getTableRef ? this.fromObject.getTableRef() : DatasourceUtil.getTableRef(this.module, this.projectedItems.map(item => item.getItemId())));
			return this.tableRef;
		}

		getOrigin () {
			return this.fromObject.getOrigin();
		}

		getModule () {
			return this.module;
		}

		setModule (module) {
			this.module = module;
		}

		getSynchDataEntry () {
			return this.synchDataEntry;
		}

		setSynchDataEntry (value) {
			this.synchDataEntry = value;
		}

		/**
		 * todo: livewidget_cleanup: we should remove this since it is still using 
		 * deprecated visAPI.getdataSlots
		 */
		getProjectedItems () {
			if (this.projectedItems) {
				return this.projectedItems;
			} else if (this.visAPI) {
				this.projectedItems = this.visAPI.getDataSlots();
			}
			return this.projectedItems;
		}

		/**
		 * todo: livewidget_cleanup: we should remove this since it is still using 
		 * deprecated visAPI
		 */
		setProjectedItems (items = []) {
			this.projectedItems = items;
		}

		mergeProjectedItems (contextEntry) {
			let itemsToMerge = contextEntry.getProjectedItems();
			this.projectedItems = this.projectedItems || [];
			itemsToMerge.forEach(itemToMerge => {
				var exist = _.find(this.projectedItems, item => {
					return itemToMerge.getItemId() === item.getItemId();
				});
				if (!exist) {
					this.projectedItems.push(itemToMerge);
				}
			});

			this.tableRef = _.uniq(this.getTableRef().concat(contextEntry.getTableRef()));
		}

		haveJoinedTables (synchContextToVerify) {
			return SynchContextHelper.haveJoinedTables(this, synchContextToVerify);
		}

		getTableNames () {
			return this.module.getTableNames();
		}

		addWidgetId(id) {
			if (this.widgetIds.indexOf(id) === -1) {
				this.widgetIds.push(id);
			}
		}

		getWidgetIds () {
			return this.widgetIds;
		}
	}

	class SynchContextHelper {
		static createNewSynchContext (options = {}) {
			return DatasourceUtil.isSupportedNonJoinTableDatascourceType(
				options.module,
				options.projectedItems ? options.projectedItems.map(item => item.getItemId()): null) ? new SynchContext(options) : null;
		}

		static getNetSlotsToClearBrushing (options = {}) {
			const sourceModule = options.sourceModule;
			const targetModule = options.targetModule;
			const dataSlots = options.dataSlots || [];
			const tableNames = options.tableNames || [];
			const pageContext = options.pageContext;
			const selector = options.selector;
			const itemIds = options.itemIds || [];
			const newSelectionIsEmpty = options.newSelectionIsEmpty;
			const newSectionFromSource = options.newSectionFromSource;

			const projectedItemIds = _getProjectedDataItems(dataSlots);
			let nonProjectedItems = _.difference(itemIds, projectedItemIds);
			const selectionFromProjectedItems = nonProjectedItems.length === 0;

			if (selectionFromProjectedItems && !newSectionFromSource) {
				return dataSlots;
			}

			const netPageContext = pageContext.getNetPageContext(selector) ;
			//Gather all brushed hierarchies
			let potentialItemIds = [];
			netPageContext.forEach(entry => {
				potentialItemIds = potentialItemIds.concat(_.pluck(entry.hierarchies, 'hierarchyUniqueName'));
			});

			const columnNames = _getColumnNames(sourceModule, targetModule, tableNames, newSelectionIsEmpty ? potentialItemIds : itemIds);
			nonProjectedItems = _retrieveItems(targetModule.getMetadataColumns(getMetadataColumnsCallback), columnNames, false, tableNames);

			if (newSelectionIsEmpty) {
				const slotsToClear = _.clone(dataSlots);
				if (nonProjectedItems.length > 0) {
					slotsToClear.push(new SynchSlotAPI({ itemIds: nonProjectedItems }));
				}
				return slotsToClear;
			} else {
				//Get a list of column names
				return nonProjectedItems.length === 0 ? [] : [new SynchSlotAPI({ itemIds: nonProjectedItems })];
			}
		}

		static getKey (sourceId, tableRef) {
			return sourceId + (tableRef ? tableRef.join('_') : '');
		}

		static haveJoinedTables (firstSynchContext, secondSynchContext) {
			if (firstSynchContext.getSourceId() !== secondSynchContext.getSourceId()) {
				//Different data sources do not have joint table relationships
				return false;
			}

			const module = firstSynchContext.getModule();
			const tableNames = firstSynchContext.getTableRef();
			const otherTableNames = secondSynchContext.getTableRef();

			return DatasourceUtil.haveTableJoinsInSameDataSource(module, tableNames, otherTableNames);
		}

		static createSynchProjectedItems (module, projectedItems = []) {
			let results = [];
			if (module) {
				projectedItems.forEach(item => {
					if (!DatasourceUtil.isMultiMeasuresSeriesOrValue(item.getItemId())){
						results.push(new SynchProjectedItem(module, item));
					}
				});
			}
			return results;
		}

		static replaceUseValue (column, value) {
			var regex = new RegExp('([^->].*)(->.*)');
			if (regex.test(value.u)) {
				value.u = value.u.replace(regex, column.getId()+'$2');
				return true;
			}
		}
	}

	return SynchContextHelper;
});
